import React, { useCallback, useEffect, useRef, useState } from "react"
import { Form, Button } from "reactstrap"
import Select from "components/Shared/TranslatedSelect"
import { Slider } from "primereact/slider"
//Import Flatepicker
import "flatpickr/dist/themes/material_blue.css"
import LocalizedFlatpickr from "components/Common/LocalizedFlatpickr"
import { useTranslation } from "react-i18next"
import { showAlert } from "store/reducers/alertReducer"
import { useDispatch } from "react-redux"
import {
  formatCalendarDateRangeForFilter,
  parseCalendarDateString,
} from "utils/calendarDateUtils"

const FILTER_SELECT_MENU_PROPS = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
}

const parseFilterDate = value => {
  if (!value || value instanceof Date) {
    return value instanceof Date && !Number.isNaN(value.getTime()) ? value : null
  }
  const match = String(value)
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/)
  if (!match) return null
  const [, year, month, day, hour = "0", minute = "0"] = match
  // en-CA with hour12:false formats midnight as 24:00 on the same calendar day.
  let hourNum = Number(hour)
  if (hourNum === 24) hourNum = 0
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    hourNum,
    Number(minute),
    0,
    0
  )
  return Number.isNaN(date.getTime()) ? null : date
}

const syncFlatpickrRange = (instanceRef, range) => {
  const fp = instanceRef.current?.flatpickr
  if (!fp) return

  const hasCompleteRange = Boolean(range?.min && range?.max)
  if (!hasCompleteRange) {
    // A single selected day is an in-progress range, not a reset.
    if (fp.selectedDates.length === 1) return
    if (fp.selectedDates.length > 0) fp.clear(false)
    return
  }

  const nextDates = [parseFilterDate(range.min), parseFilterDate(range.max)]
  if (nextDates.some(date => !date)) return

  const current = fp.selectedDates
  const matches =
    current.length === 2 &&
    current[0].getTime() === nextDates[0].getTime() &&
    current[1].getTime() === nextDates[1].getTime()
  if (matches) return

  fp.setDate(nextDates, false)
}

const uniqueSelectOptions = options => {
  const seen = new Set()
  const unique = []
  ;(options || []).forEach(option => {
    const key = `${String(option?.value)}:${String(option?.label ?? "").trim()}`
    if (seen.has(key)) return
    seen.add(key)
    unique.push(option)
  })
  return unique
}

// An option labelled like the placeholder (e.g. "All") replaces it. Rendering
// both shows a duplicate, and the placeholder's "" value is sent as status 0.
const findPlaceholderOption = filter =>
  (filter.options || []).find(option => option.label === filter.placeholder)

const getSelectValue = (filter, selectedValue) => {
  if (selectedValue !== undefined && selectedValue !== null && selectedValue !== "") {
    return selectedValue
  }
  return findPlaceholderOption(filter)?.value ?? ""
}

const filterSelectStyles = {
  menuPortal: base => ({ ...base, zIndex: 9999 }),
  menu: base => ({
    ...base,
    zIndex: 9999,
    width: "100%",
    minWidth: "100%",
  }),
  menuList: base => ({
    ...base,
    maxHeight: 220,
    overflowY: "auto",
    overflowX: "hidden",
  }),
  option: base => ({
    ...base,
    whiteSpace: "normal",
    wordBreak: "break-word",
    width: "100%",
    maxWidth: "100%",
  }),
  singleValue: base => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
}

const Filters = ({
  filters,
  searchTerm,
  appVersion = false,
  handleSearchChange,
  handleFilterChange,
  handleSearch,
  onReset = () => {},
  isCustomPageSize,
  selectedFilters,
  pageSizes,
  itemsPerPage,
  setItemsPerPage,
  isRangeSlider,
  priceRange,
  setPriceRange,
  hideSearch,
  isRangeDate,
  dateRange,
  setDateRange,
  flatpickrRef,
  searchBy,
  colSize,
  onInputChange,
  isAgeRange = false,
  ageRange,
  setAgeRange = "",
  tempAgeRange = {},
  setTempAgeRange = {},
  handleAppVersionChange = () => {},
  isSecondSearch = false,
  searchTerm2 = "",
  searchBy2 = "",
  handleSearchChange2 = () => {},
  onInputChangeSingle = () => {},
  dateRangeWithTime = false,
  loading = false,
  isEstimatedDeliveryDateRange = false,
  estimatedDeliveryDateRange = {},
  setEstimatedDeliveryDateRange = () => {},
  estimatedDeliveryFlatpickrRef = null,
}) => {
  const { t, i18n } = useTranslation()
  const isRtl = i18n.language === "sa"
  const hasEstimatedDeliveryValue = Boolean(
    estimatedDeliveryDateRange.min && estimatedDeliveryDateRange.max
  )
  const [menuIsOpen, setMenuIsOpen] = useState(false)
  const [selectedMenuOpen, setSelectedMenuOpen] = useState(null)

  const dispatch = useDispatch()

  const handleBlur = () => {
    // Close the dropdown when clicking outside
    setMenuIsOpen(false)
  }

  const prevSearchTermRef = useRef("")
  const prevSearchTermRef2 = useRef("")
  const datePickerRef = useRef(null)
  const estimatedPickerRef = useRef(null)
  const initialDateRangeRef = useRef(
    dateRange?.min && dateRange?.max ? [dateRange.min, dateRange.max] : []
  )
  const initialEstimatedRangeRef = useRef(
    estimatedDeliveryDateRange?.min && estimatedDeliveryDateRange?.max
      ? [
          parseCalendarDateString(estimatedDeliveryDateRange.min),
          parseCalendarDateString(estimatedDeliveryDateRange.max),
        ].filter(Boolean)
      : []
  )

  const setDatePickerNode = useCallback(
    node => {
      datePickerRef.current = node
      if (!flatpickrRef) return
      if (typeof flatpickrRef === "function") flatpickrRef(node)
      else flatpickrRef.current = node
    },
    [flatpickrRef]
  )
  const setEstimatedPickerNode = useCallback(
    node => {
      estimatedPickerRef.current = node
      if (!estimatedDeliveryFlatpickrRef) return
      if (typeof estimatedDeliveryFlatpickrRef === "function") {
        estimatedDeliveryFlatpickrRef(node)
      } else {
        estimatedDeliveryFlatpickrRef.current = node
      }
    },
    [estimatedDeliveryFlatpickrRef]
  )

  useEffect(() => {
    if (!isRangeDate) return
    syncFlatpickrRange(datePickerRef, dateRange)
  }, [isRangeDate, dateRange, dateRange?.min, dateRange?.max])

  useEffect(() => {
    if (!isEstimatedDeliveryDateRange) return
    syncFlatpickrRange(estimatedPickerRef, estimatedDeliveryDateRange)
  }, [
    isEstimatedDeliveryDateRange,
    estimatedDeliveryDateRange?.min,
    estimatedDeliveryDateRange?.max,
  ])

  const handleSearchClick = () => {
    // Gather selected filter values and search term
    // const selectedFilters = {}
    // filters.forEach(filter => {
    //   if (filter.type === "select") {
    //     selectedFilters[filter.name] = filter.selectedValue
    //   }
    // })
    handleSearch()
  }

  useEffect(() => {
    if (isAgeRange) {
      const timer = setTimeout(() => {
        if (tempAgeRange.min !== "" && tempAgeRange.max !== "") {
          const min = parseInt(tempAgeRange.min)
          const max = parseInt(tempAgeRange.max)

          // Ensure min is not greater than max
          if (min > max) {
            dispatch(
              showAlert({
                text: t("Min age cannot be greater than Max age"),
                type: "error",
              })
            )
          } else {
            setAgeRange({ min, max })
          }
        }
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [tempAgeRange])

  const handleTempAgeChange = (e, type) => {
    const value = e.target.value
    setTempAgeRange(prev => ({
      ...prev,
      [type]: value,
    }))
  }

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === "Enter") {
        // Prevent default behavior if inside a textarea
        if (e.target.tagName === "TEXTAREA") return

        e.preventDefault()
        handleSearch() // Trigger search
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [handleSearch])

  useEffect(() => {
    if (prevSearchTermRef.current !== "" && searchTerm?.trim() === "") {
      handleSearch() // Trigger search only when searchTerm is cleared
    }

    prevSearchTermRef.current = searchTerm // Update previous value
  }, [searchTerm])

  useEffect(() => {
    if (prevSearchTermRef2.current !== "" && searchTerm2.trim() === "") {
      handleSearch() // Trigger search only when searchTerm is cleared
    }

    prevSearchTermRef2.current = searchTerm2 // Update previous value
  }, [searchTerm2])

  return (
    <Form onSubmit={e => e.preventDefault()}>
      <div
        className="mb-3 pb-3 border-bottom portal-filter-bar"
        data-col-size={colSize || 4}
      >
        {hideSearch ? null : (
          <div className="portal-filter-bar__item">
            <div className="filter-search-field">
              <i className="bx bx-search-alt-2 filter-search-icon" aria-hidden="true" />
              <input
                name="search"
                disabled={loading}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={searchBy ? searchBy : t("Keyword")}
                title={searchBy ? searchBy : t("Keyword")}
                className="form-control filter-search-input"
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleSearch()
                  }
                }}
              />
            </div>
          </div>
        )}
        {isSecondSearch && (
          <div className="portal-filter-bar__item">
            <div className="filter-search-field">
              <i className="bx bx-search-alt-2 filter-search-icon" aria-hidden="true" />
              <input
                name="search"
                type="text"
                value={searchTerm2}
                onChange={handleSearchChange2}
                placeholder={searchBy2}
                title={searchBy2}
                className="form-control filter-search-input"
                disabled={loading}
              />
            </div>
          </div>
        )}
        {filters.map((filter, index) => (
          <div key={index} className="portal-filter-bar__item">
            {filter.type === "select" && (
              <select
                className="form-select"
                disabled={loading || filter.disabled}
                value={getSelectValue(filter, selectedFilters[filter.name])}
                onChange={e => {
                  const raw = e.target.value
                  if (raw === "") {
                    handleFilterChange("", filter.name)
                    return
                  }
                  const matched = (filter.options || []).find(
                    option => String(option.value) === String(raw)
                  )
                  handleFilterChange(
                    matched ? matched.value : raw,
                    filter.name
                  )
                }}
              >
                {!filter.hidePlaceholder &&
                  filter.name !== "sortOrder" &&
                  !findPlaceholderOption(filter) && (
                    <option value="" disabled={loading}>
                      {filter.placeholder}
                    </option>
                  )}
                {uniqueSelectOptions(filter.options).map((option, index) => (
                  <option disabled={loading} key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {filter.type === "multiSelect" && (
              <Select
                value={selectedFilters[filter.name]}
                isMulti={true}
                onChange={selectedOption => {
                  handleFilterChange(selectedOption, filter.name)
                }}
                options={filter.options}
                placeholder={filter.placeholder}
                className="select2-selection"
                isDisabled={loading}
                noOptionsMessage={() => t("No options")}
                onInputChange={onInputChange}
                closeMenuOnSelect={false}
                styles={filterSelectStyles}
                {...FILTER_SELECT_MENU_PROPS}
              />
            )}
            {filter.type === "singleSelect" && (
              <Select
                value={selectedFilters[filter.name]}
                isMulti={false}
                onChange={selectedOption => {
                  handleFilterChange(selectedOption, filter.name)
                }}
                options={filter.options}
                isDisabled={loading}
                placeholder={filter.placeholder}
                className="select2-selection"
                isSearchable={filter.isSearchable ?? false}
                menuPlacement="auto"
                noOptionsMessage={() => t("No options")}
                onInputChange={onInputChangeSingle}
                styles={filterSelectStyles}
                {...FILTER_SELECT_MENU_PROPS}
              />
            )}
          </div>
        ))}
        {isRangeDate && (
          <div className="portal-filter-bar__item">
            <LocalizedFlatpickr
              className="form-control d-block"
              placeholder={t("Date")}
              disabled={loading}
              ref={setDatePickerNode}
              options={{
                mode: "range",
                enableTime: dateRangeWithTime,
                time_24hr: dateRangeWithTime,
                dateFormat: dateRangeWithTime ? "Y-m-d H:i" : "Y-m-d",
                defaultDate: initialDateRangeRef.current,
                minuteIncrement: 1,
              }}
              onChange={selectedDates => {
                const formattedDates = selectedDates.map(date => {
                  if (dateRangeWithTime) {
                    const pad = value => String(value).padStart(2, "0")
                    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
                  } else {
                    return date
                      .toLocaleDateString("en-CA", {
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                      })
                      .split("/")
                      .reverse()
                      .join("-") // Format to "yyyy-mm-dd"
                  }
                })
                const minDate = formattedDates[0]
                const maxDate = formattedDates[1]
                setDateRange({
                  min: minDate,
                  max: maxDate,
                })
              }}
              render={({ ...props }, ref) => (
                <input
                  {...props}
                  ref={ref}
                  dir="ltr"
                  style={{ unicodeBidi: "bidi-override" }}
                  onKeyDownCapture={e => {
                    if (e.key === "Backspace" || e.key === "Delete") {
                      e.preventDefault()
                      e.stopPropagation()
                    }
                  }}
                />
              )}
            />
          </div>
        )}

        {isRangeSlider && (
          <div className="portal-filter-bar__item">
            <div className="portal-filter-range">
              <input
                value={priceRange[0]}
                disabled={loading}
                onChange={e => {
                  const newValue = parseInt(e.target.value)
                  if (!isNaN(newValue)) {
                    setPriceRange([
                      Math.min(Math.max(newValue, 0), 1000),
                      priceRange[1],
                    ])
                  }
                }}
                className="form-control"
                type="number"
                placeholder={t("Min Price")}
                title={t("Min Price")}
              />
              <input
                value={priceRange[1]}
                type="number"
                disabled={loading}
                onChange={e => {
                  const newValue = parseInt(e.target.value)
                  if (!isNaN(newValue)) {
                    setPriceRange([priceRange[0], Math.min(newValue, 1000)])
                  }
                }}
                className="form-control"
                placeholder={t("Max Price")}
                title={t("Max Price")}
              />
            </div>
            <Slider
              value={priceRange}
              onChange={e => setPriceRange([e.value[0], e.value[1]])}
              className="w-full"
              range
              min={0}
              max={1000}
            />
          </div>
        )}

        {isAgeRange && (
          <>
            <div className="portal-filter-bar__item">
              <input
                value={tempAgeRange.min}
                onChange={e => handleTempAgeChange(e, "min")}
                type="number"
                placeholder={t("Min Age")}
                title={t("Min Age")}
                disabled={loading}
                className="form-control"
              />
            </div>
            <div className="portal-filter-bar__item">
              <input
                value={tempAgeRange.max}
                onChange={e => handleTempAgeChange(e, "max")}
                type="number"
                placeholder={t("Max Age")}
                title={t("Max Age")}
                disabled={loading}
                className="form-control"
              />
            </div>
          </>
        )}

        {isEstimatedDeliveryDateRange && (
          <div className="portal-filter-bar__item">
            <LocalizedFlatpickr
              className="form-control d-block estimated-delivery-date-filter"
              placeholder={t("Estimated Delivery Date")}
              disabled={loading}
              ref={setEstimatedPickerNode}
              options={{
                mode: "range",
                dateFormat: "Y-m-d",
                defaultDate: initialEstimatedRangeRef.current,
              }}
              onChange={selectedDates => {
                if (selectedDates.length === 2) {
                  const range = formatCalendarDateRangeForFilter(selectedDates)
                  if (range) {
                    setEstimatedDeliveryDateRange(range)
                  }
                } else if (selectedDates.length === 0) {
                  setEstimatedDeliveryDateRange({})
                }
              }}
              render={({ ...props }, ref) => (
                <input
                  {...props}
                  ref={ref}
                  dir={hasEstimatedDeliveryValue || !isRtl ? "ltr" : "rtl"}
                  style={
                    hasEstimatedDeliveryValue
                      ? { unicodeBidi: "bidi-override" }
                      : isRtl
                      ? { textAlign: "right" }
                      : undefined
                  }
                  onKeyDownCapture={e => {
                    if (e.key === "Backspace" || e.key === "Delete") {
                      e.preventDefault()
                      e.stopPropagation()
                    }
                  }}
                />
              )}
            />
          </div>
        )}

        <div className="portal-filter-bar__actions">
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="success"
              onClick={handleSearchClick}
              disabled={loading}
              className="g_center d-flex portal-filter-bar__btn"
              style={{ minWidth: 40, height: 39, padding: "0 10px" }}
            >
              <i
                className="bx bx-search-alt-2"
                style={{ fontSize: 22 }}
              ></i>
              {t("Search")}
            </Button>
            <Button
              type="button"
              color="success"
              onClick={onReset}
              disabled={loading}
              className="g_center d-flex btn-light portal-filter-bar__btn"
              style={{ minWidth: 40, height: 39, padding: "0 10px" }}
            >
              <i className="mdi mdi-refresh" style={{ fontSize: 22 }}></i>
              {t("Reset")}
            </Button>
          </div>
        </div>
      </div>
    </Form>
  )
}

export default Filters

/* {isCustomPageSize && (
          <Col>
            <select
              className="form-select pageSize mb-2"
              value={itemsPerPage}
              onChange={e => {
                setItemsPerPage(e.target.value)
              }}
            >
              {pageSizes.map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )} */
/* {isCustomPageSize && (
          <Col>
            <select
              className="form-select pageSize mb-2"
              value={itemsPerPage}
              onChange={e => {
                setItemsPerPage(e.target.value)
              }}
            >
              {pageSizes.map(pageSize => (
                <option key={pageSize} value={pageSize}>
                  Show {pageSize}
                </option>
              ))}
            </select>
          </Col>
        )} */
